import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import {
  authService,
  signIn,
  signOut,
  isAuthenticated as checkIsAuthenticated,
  getUserInfo,
  User,
} from "../services/authService";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        await authService.initialize();

        const authenticated = await checkIsAuthenticated();
        setIsAuthenticated(authenticated);

        if (authenticated) {
          const userInfo = await getUserInfo();
          setUser(userInfo);
        } else {
          setUser(null);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      login: signIn,
      logout: async (): Promise<void> => {
        try {
          setUser(null);
          setIsAuthenticated(false);
          await signOut();
        } catch (error) {
          window.location.href = "/";
        }
      },
    }),
    [isAuthenticated, isLoading, user],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Helper hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
